import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { notifications } from '@mantine/notifications';
import { adminService } from './adminService';
import { customerKeys } from '@api/customers/customerQueries';
import { systemKeys } from '@api/systems/systemQueries';
import type { ListLicenseViewerQuery } from '@features/admin/domain/admin.schemas';

export const adminKeys = {
  all: ['admin'] as const,
  metrics: () => [...adminKeys.all, 'metrics'] as const,
  licenses: (query: ListLicenseViewerQuery) => [...adminKeys.all, 'licenses', query] as const,
};

export const adminMetricsQueryOptions = () => queryOptions({
  queryKey: adminKeys.metrics(),
  queryFn: () => adminService.getAdminMetrics(),
  staleTime: 2 * 60 * 1000, // 2 minutes
});

export const licenseViewerQueryOptions = (query: ListLicenseViewerQuery) => queryOptions({
  queryKey: adminKeys.licenses(query),
  queryFn: () => adminService.listLicenses(query),
  placeholderData: keepPreviousData,
});

export function useDeleteCustomerMutation() {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, string>({
    mutationFn: (customerId) => adminService.deleteCustomer(customerId),
    onSuccess: () => {
      notifications.show({
        title: 'Customer Deleted',
        message: 'Customer and associated data have been removed',
        color: 'green',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: customerKeys.lists()});
      queryClient.invalidateQueries({queryKey: adminKeys.metrics()});
    },
  });
}

export function useDeleteSystemMutation() {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, string>({
    mutationFn: (systemId) => adminService.deleteSystem(systemId),
    onSuccess: () => {
      notifications.show({
        title: 'System Deleted',
        message: 'System and all associated data have been removed',
        color: 'green',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: systemKeys.lists()});
      queryClient.invalidateQueries({queryKey: adminKeys.metrics()});
    },
  });
}
